import { Suspense, lazy, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore.js';
import { useNotificationStore } from '../../store/notificationStore.js';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { workspaceNav } from '../../utils/navigation.js';
import { IconGrid, IconLock, IconLogout, IconMoon, IconMore, IconSun, IconX } from '../icons/Icons.jsx';

const ChangePasswordModal = lazy(() => import('../shared/ChangePasswordModal.jsx').then((m) => ({ default: m.ChangePasswordModal })));

const PRIMARY = ['/dashboard', '/inbox', '/contacts', '/calls'];
const SHORT_LABELS = { '/dashboard': 'Home' };

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return 'U';
  return ((parts[0][0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function MoreSheet({ open, onClose, items }) {
  const [showChangePw, setShowChangePw] = useState(false);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const logout = useAuthStore((s) => s.logout);
  const { theme, toggle } = useTheme();
  const brandName = user?.role === 'PLATFORM_ADMIN' ? 'Master Platform' : tenant?.brandName || tenant?.name || 'Workspace';

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0" style={{ background: 'var(--overlay)' }} onClick={onClose} />
          <div className="absolute inset-x-0 bottom-0 rounded-t-[20px] p-4 safe-bottom max-h-[85dvh] overflow-y-auto" style={{ background: 'var(--card)', boxShadow: 'var(--shadowFloat)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3 min-w-0">
                <span className="w-11 h-11 rounded-full grid place-items-center text-sm font-bold flex-shrink-0" style={{ background: 'var(--accentSoft)', color: 'var(--accentHot)' }}>
                  {initials(user?.name || user?.email)}
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate" style={{ color: 'var(--text)' }}>{user?.name || 'User'}</p>
                  <p className="text-xs truncate" style={{ color: 'var(--textMuted)' }}>{brandName}</p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="tap-target grid place-items-center rounded-xl" style={{ color: 'var(--textMuted)' }} aria-label="Close menu">
                <IconX size={18} />
              </button>
            </div>

            {items.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {items.map(({ path, label, Icon }) => (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={onClose}
                    className="flex flex-col items-center justify-center gap-1.5 rounded-[12px] py-3 text-xs font-semibold"
                    style={({ isActive }) => ({
                      background: isActive ? 'var(--accentSoft)' : 'var(--bg)',
                      color: isActive ? 'var(--accentHot)' : 'var(--text)',
                    })}
                  >
                    <Icon size={20} />
                    {label}
                  </NavLink>
                ))}
              </div>
            )}

            <div className="grid gap-1">
              <button type="button" onClick={toggle} className="menu-item !min-h-[44px]">
                {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </button>
              <button type="button" onClick={() => setShowChangePw(true)} className="menu-item !min-h-[44px]">
                <IconLock size={18} /> Change password
              </button>
              <button type="button" onClick={async () => { onClose(); await logout(); }} className="menu-item !min-h-[44px]" style={{ color: 'var(--danger)' }}>
                <IconLogout size={18} /> Log out
              </button>
            </div>
          </div>
        </div>
      )}
      <Suspense fallback={null}>
        <ChangePasswordModal isOpen={showChangePw} onClose={() => setShowChangePw(false)} />
      </Suspense>
    </>
  );
}

const tabClass = 'flex-1 tap-target flex flex-col items-center justify-center py-1.5 gap-0.5 text-[11px] font-semibold transition-colors relative';

export function MobileNav() {
  const [moreOpen, setMoreOpen] = useState(false);
  const user = useAuthStore((s) => s.user);
  const tenant = useAuthStore((s) => s.tenant);
  const unread = useNotificationStore((s) => s.unreadConversations);
  const location = useLocation();
  const isOwner = user?.role === 'PLATFORM_ADMIN';

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const all = isOwner
    ? [{ path: '/dashboard', label: 'Owner', Icon: IconGrid }]
    : workspaceNav(tenant, user?.role).flatMap((s) => s.items);
  const primary = all.filter((item) => PRIMARY.includes(item.path));
  const rest = all.filter((item) => !PRIMARY.includes(item.path));
  const moreActive = rest.some((item) => location.pathname.startsWith(item.path));

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 flex" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {primary.map(({ path, label, Icon }) => (
        <NavLink key={path} to={path} className={tabClass} style={({ isActive }) => ({ color: isActive ? 'var(--accent)' : 'var(--textMuted)' })}>
          <span className="relative">
            <Icon size={21} />
            {path === '/inbox' && unread > 0 && (
              <span className="absolute -top-1.5 -right-2.5 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold grid place-items-center" style={{ background: 'var(--danger)', color: '#fff' }}>
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </span>
          <span>{SHORT_LABELS[path] || label}</span>
        </NavLink>
      ))}
      <button type="button" onClick={() => setMoreOpen(true)} className={tabClass} style={{ color: moreActive ? 'var(--accent)' : 'var(--textMuted)' }} aria-label="More">
        <IconMore size={21} />
        <span>More</span>
      </button>
      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} items={rest} />
    </nav>
  );
}
